import { useTheme } from "@hooks/useTheme";
import { useEffect, useRef, useState } from "react";
import AddVerificationReportForm from "./AddVerificationReportForm";
import { useMutationData } from "../../hooks/useMutationData";
import { VERIFICATION_TYPE } from "../../constants";
import queryKeys from "../../configs/react-query/query-key";
import { resolveVerificationToolCdForReport } from "@utils/common";
import { OutlineButton, PrimaryButton } from "@components/Buttons";
import { useTranslation } from "react-i18next";
import ToastConstant from "../../configs/react-toastify/snackbar";
import { TOAST } from "../../constants";
import { PROGRESS_VERIFICATION_REPORT_STATUS } from "../../constants/verificationReport";
import useEscapeKey from "@hooks/useEscapeKey";

const AddVerificationReportModal = ({
  isOpen,
  onClose,
  onSave,
  projectId = null,
  verificationVersion = null,
  verificationToolCd = "CLARITY",
  "data-testid": dataTestId = "add-verification-report-modal",
}) => {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const formRef = useRef(null);
  const [isFormValid, setIsFormValid] = useState(false);
  const [verificationFile, setVerificationFile] = useState(null);
  const [loading, setLoading] = useState(false);

  const getSourceCodeVerificationsWithClarity = useMutationData(
    queryKeys.getSourceCodeVerificationsWithClarity,
    {
      method: "POST",
    },
    {
      onSuccess: (response) => {
        if (typeof response !== "string") {
          const files = response.object || [];

          const isClarityAirRow = (f) =>
            f.verificationType === "clarity_air" || f.verificationType === "clarityair";
          const isClarityRow = (f) =>
            f.verificationType === "clarity" || (f.clarityFlag === "Y" && !isClarityAirRow(f));
          const isFossidRow = (f) =>
            f.verificationType === "fossid" || (f.clarityFlag !== "Y" && !isClarityAirRow(f));

          let filteredFile = null;
          if (verificationToolCd === VERIFICATION_TYPE.CLARITY) {
            filteredFile = files.find(isClarityRow);
          } else if (verificationToolCd === VERIFICATION_TYPE.CLARITYAIR) {
            filteredFile = files.find(isClarityAirRow);
          } else {
            filteredFile = files.find(isFossidRow);
          }

          setVerificationFile(filteredFile || null);
        }
        setLoading(false);
      },
      onError: (error) => {
        setLoading(false);
        console.error("Error fetching binary verifications:", error);
      },
    },
  );

  // API mutation for creating verification report
  const createVerificationReportMutation = useMutationData(
    "/verification_report",
    { method: "POST" },
    {
      onSuccess: () => {
        ToastConstant(TOAST.success, t("toast.verification-report.added-success.title"), t("toast.verification-report.added-success.message"));
        onClose();
        if (onSave) {
          onSave(); // Call onSave to refresh the list
        }
      },
      onError: (error) => {
        console.error("Error adding verification report:", error);
        ToastConstant(TOAST.error, t("toast.verification-report.add-failed.title"), t("toast.verification-report.add-failed.message"));
      },
    },
  );

  // Fetch verification files when modal opens with projectId and verificationVersion
  useEffect(() => {
    if (isOpen && projectId && verificationVersion) {
      setLoading(true);
      getSourceCodeVerificationsWithClarity.mutate({
        projectId: projectId,
        verificationVersion: verificationVersion,
        startRow: 0,
        pageSize: 10,
      });
    }
  }, [isOpen, projectId, verificationVersion]);

  // Check form validity whenever form data changes
  useEffect(() => {
    const checkFormValidity = () => {
      if (formRef.current) {
        const formData = formRef.current.getFormData();
        const isValid =
          formData.verificationReportName.trim() !== "" &&
          formData.noticeInfoCompletionDate &&
          formData.verificationFile != null;
        setIsFormValid(isValid);
      }
    };

    // Check immediately
    checkFormValidity();

    // Set up interval to check periodically (since we can't directly listen to form changes)
    const interval = setInterval(checkFormValidity, 100);

    return () => clearInterval(interval);
  }, [isOpen]);

  const transformComponentData = (data) => {
    if (!data || !Array.isArray(data)) return [];
    return data.map((item) => ({
      ...item,
      componentName: item.component || item.componentName,
      licenseName: item.componentLicense || item.licenseName || "",
    }));
  };

  const handleSave = () => {
    if (formRef.current) {
      const formData = formRef.current.getFormData();

      // Validate form data
      if (!formData.verificationReportName.trim()) {
        ToastConstant(TOAST.error, t("toast.validation.enter-report-name.title"), t("toast.validation.enter-report-name.message"));
        return;
      }

      if (!formData.noticeInfoCompletionDate) {
        ToastConstant(TOAST.error, t("toast.validation.select-completion-date.title"), t("toast.validation.select-completion-date.message"));
        return;
      }

      if (formData.verificationFile === null) {
        ToastConstant(TOAST.error, t("toast.validation.select-verification-project-version.title"), t("toast.validation.select-verification-project-version.message"));
        return;
      }

      // Get projectId and verificationVersion from verificationFile
      const selectedFile = formData.verificationFile;
      if (!selectedFile) {
        ToastConstant(TOAST.error, t("toast.validation.no-file-selected.title"), t("toast.validation.no-file-selected.message"));
        return;
      }

      // Extract projectId and verificationVersion from the selected file
      const projectId = selectedFile.projectId;
      const verificationVersion = selectedFile.verificationVersion;
      const scanComponentData = formData.scanComponentData;

      if (!projectId || !verificationVersion) {
        ToastConstant(TOAST.error, t("toast.validation.invalid-file-data.title"), t("toast.validation.invalid-file-data.message"));
        return;
      }

      const reportVerificationToolCd = resolveVerificationToolCdForReport(selectedFile, verificationToolCd);

      // Create the payload with the correct structure
      const requestData = {
        developmentDeptName: "",
        expectedNoticeInfoEntryCompletionDt: formData.noticeInfoCompletionDate || "",
        projectId: projectId,
        useArea: "",
        verificationReportDetails: transformComponentData(scanComponentData),
        verificationReportId: "",
        verificationReportName: formData.verificationReportName,
        verificationReportProjects: [{
          projectId: projectId,
          verificationVersion: verificationVersion
        }],
        verificationReportResultCd: "",
        verificationReportStatusCd: PROGRESS_VERIFICATION_REPORT_STATUS.WRITE,
        verificationToolCd: reportVerificationToolCd,
        verificationVersion: verificationVersion,
        reportFileName: selectedFile.realFileName,
      };

      // Call the API to create the verification report
      createVerificationReportMutation.mutate(requestData);
    }
  };

  useEscapeKey(onClose, isOpen);

  if (!isOpen) return null;

  return (
    <div className={`fixed inset-0 ${colors.bgBlurModal} z-50 flex items-center justify-start xl:justify-center p-4 backdrop-blur-[1px] overflow-x-auto`} data-testid={`${dataTestId}-overlay`}>
      <div
        className={`${colors.bgModal} w-[90vw] min-w-[1200px] max-h-[90vh] flex-shrink-0 overflow-auto rounded-lg shadow-xl`}
        data-testid={dataTestId}
      >
        {/* Modal Header */}
        <div className={`px-6 py-4 border-b ${colors.borderPrimary}`} data-testid={`${dataTestId}-header`}>
          <div className="flex items-center justify-between">
            <h2 className={`${colors.textPrimary} text-xl font-bold uppercase`} data-testid={`${dataTestId}-title`}>{t("project.verification-report.add-verif-report")}</h2>
          </div>
        </div>

        {/* Modal Content */}
        <div className="p-6" data-testid={`${dataTestId}-content`}>
          <AddVerificationReportForm
            ref={formRef}
            initVerificationFile={verificationFile}
            dataTestId={`${dataTestId}-form`}
          />
        </div>

        {/* Modal Footer */}
        <div className={`px-6 py-4 border-t ${colors.borderPrimary}`} data-testid={`${dataTestId}-footer`}>
          <div className="flex items-center justify-end space-x-3">
            <OutlineButton data-testid={`${dataTestId}-cancel-button`} onClick={onClose}>
              {t("common.cancel-button")}
            </OutlineButton>
            <PrimaryButton
              data-testid={`${dataTestId}-confirm-button`}
              onClick={handleSave}
              disabled={!isFormValid || createVerificationReportMutation.isPending}
            >
              {t("common.save-button")}
            </PrimaryButton>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddVerificationReportModal;
